import { Injectable, NgZone, inject } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { Firestore, collection, collectionGroup, doc, getDoc, getDocs, or, query, where } from '@angular/fire/firestore';
import { NetworkConstants, FirestoreUser, deepCopy } from 'diwimamodelweb/lib';
import { UserGroup } from 'diwimamodelweb/lib/network/user-group.data';
import { SettingsService } from 'src/app/shared/settings.service';
import { UserGroupsService } from 'src/app/cockpit/us-managment/user-groups.service';

const GROUP_ID_PREFIX = 'group:';

@Injectable({ providedIn: 'root' })
export class UserNameLookupService {
  private firestore = inject(Firestore);
  private zone = inject(NgZone);
  private cache$ = new BehaviorSubject<Record<string, string>>({});
  private inflight = new Set<string>();

  private usersSubject$ = new BehaviorSubject<FirestoreUser[]>([]);
  private usersLoadedFor?: string;
  private usersLoading?: Promise<void>;

  private groupsLoadedFor?: string;
  private groupMembers = new Map<string, string[]>();
  private groupMemberLoads = new Map<string, Promise<string[]>>();

  names$: Observable<Record<string, string>> = this.cache$.asObservable();
  users$: Observable<FirestoreUser[]>;
  groups$: Observable<UserGroup[]>;

  constructor(
    private settingsService: SettingsService,
    private userGroupsService: UserGroupsService,
  ) {
    this.users$ = this.usersSubject$.pipe(map(list => this.sortUsers(list)));
    this.groups$ = this.userGroupsService.allAssignableUserGroupsSubject$.pipe(
      map(list => this.sortGroups(list ?? [])),
    );

    this.groups$.subscribe(list => list.forEach(g => g?.identifier && this.loadGroupMembers(g.identifier)));
    this.usersSubject$.subscribe(users => this.cacheNames(users));
    combineLatest([
      this.settingsService.selectedUserContext$,
      this.settingsService.superContext,
    ]).subscribe(([ctx]) => {
      if (ctx?.identifier) this.loadContextUsers(ctx.identifier);
    });
  }

  ensureAllUsersLoaded(): void {
    const ctxId = this.settingsService.selectedUserContext$?.value?.identifier;
    if (ctxId) this.loadContextUsers(ctxId);
  }

  get currentUsers(): FirestoreUser[] {
    this.ensureAllUsersLoaded();
    return this.sortUsers(this.usersSubject$.value);
  }

  get currentNames(): Record<string, string> {
    return this.cache$.value;
  }

  private assignableContextIds(ctxId: string): string[] {
    if (!this.settingsService.superContext?.value) return [ctxId];

    const fromResolver: string[] = this.settingsService.contextAndSubContextIds?.value ?? [];
    const fromContext: string[] =
      (this.settingsService.selectedUserContext$?.value as any)?.subContextIds ?? [];

    const ids: string[] = this.settingsService.selectedContextAndSubContextIds.value ?? []

    return [...new Set(ids.filter(Boolean))];
    // return [...new Set([ctxId, ...fromResolver, ...fromContext].filter(Boolean))];
  }

  private loadContextUsers(ctxId: string): void {
    const contextIds = this.assignableContextIds(ctxId);
    const key = [...contextIds].sort().join(',');
    if (this.usersLoadedFor === key) return;
    if (this.usersLoading) {
      this.usersLoading.finally(() => this.loadContextUsers(ctxId));
      return;
    }

    this.usersLoadedFor = key;
    this.usersLoading = this.fetchContextUsers(contextIds)
      .then(users => {
        if (this.settingsService.selectedUserContext$?.value?.identifier !== ctxId) return;
        if (!users.length) this.usersLoadedFor = undefined;
        this.zone.run(() => this.usersSubject$.next(users));
      })
      .finally(() => { this.usersLoading = undefined; });
  }

  refreshAllUsers(): void {
    this.usersLoadedFor = undefined;
    this.ensureAllUsersLoaded();
  }

  private static readonly CONTEXT_CHUNK_SIZE = 30;

  private chunkContextIds(ids: string[]): string[][] {
    const size = UserNameLookupService.CONTEXT_CHUNK_SIZE;
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += size) chunks.push(ids.slice(i, i + size));
    return chunks;
  }

  private async fetchContextUsers(contextIds: string[]): Promise<FirestoreUser[]> {
    const byId = new Map<string, any>();
    const add = (id: string, data: any) => {
      if (id && !byId.has(id)) byId.set(id, { ...data, identifier: data?.identifier ?? id });
    };

    const usersRef = collection(this.firestore, NetworkConstants.COLLECTION_USER);

    for (const chunk of this.chunkContextIds(contextIds)) {
      const queries = chunk.length === 1
        ? [query(usersRef, or(
            where('contextId', '==', chunk[0]),
            where('contextIds', 'array-contains', chunk[0]),
          ))]
        : [
            query(usersRef, where('contextId', 'in', chunk)),
            query(usersRef, where('contextIds', 'array-contains-any', chunk)),
          ];

      await Promise.all(queries.map(async q => {
        try {
          const snap = await getDocs(q);
          snap.docs.forEach(d => add(d.id, d.data()));
        } catch (err) {
          console.error(`[UserNameLookup] user query for contexts ${chunk.join(', ')} failed:`, err);
        }
      }));
    }

    // Users whose membership lives only in the sub-collection carry no contextId
    // on the user doc, so they need the membership sweep to be found at all.
    const missing = new Set<string>();
    await Promise.all(this.chunkContextIds(contextIds).map(async chunk => {
      try {
        const memberships = await getDocs(query(
          collectionGroup(this.firestore, NetworkConstants.COLLECTION_CONTEXT_MEMBERSHIP),
          chunk.length === 1
            ? where('identifier', '==', chunk[0])
            : where('identifier', 'in', chunk),
        ));
        memberships.docs
          .map(d => (d.data() as any)?.userId)
          .filter((id: any): id is string => !!id && !byId.has(id))
          .forEach(id => missing.add(id));
      } catch (err) {
        console.error(`[UserNameLookup] membership lookup for contexts ${chunk.join(', ')} failed:`, err);
      }
    }));

    await Promise.all([...missing].map(async id => {
      if (byId.has(id)) return;
      try {
        const snap = await getDoc(doc(this.firestore, NetworkConstants.COLLECTION_USER, id));
        if (snap.exists()) add(id, snap.data());
      } catch {}
    }));

    return [...byId.values()] as FirestoreUser[];
  }

  // Names

  ensureLoaded(ids: string[] | undefined | null): void {
    const cur = this.cache$.value;
    const toFetch = (ids ?? []).filter(id => !!id && !(id in cur) && !this.inflight.has(id));
    if (!toFetch.length) return;
    toFetch.forEach(id => this.inflight.add(id));

    Promise.all(toFetch.map(async id => {
      try {
        const snap = await getDoc(doc(this.firestore, NetworkConstants.COLLECTION_USER, id));
        return [id, snap.exists() ? this.displayNameOf(snap.data()) : ''] as const;
      } catch {
        return [id, ''] as const;
      } finally {
        this.inflight.delete(id);
      }
    })).then(results => {
      const next = { ...this.cache$.value };
      results.forEach(([id, name]) => { next[id] = name; });
      this.zone.run(() => this.cache$.next(next));
    });
  }

  private cacheNames(users: FirestoreUser[]): void {
    if (!users?.length) return;
    const next = { ...this.cache$.value };
    let changed = false;
    users.forEach((u: any) => {
      const name = this.displayNameOf(u);
      if (u?.identifier && name && next[u.identifier] !== name) {
        next[u.identifier] = name;
        changed = true;
      }
    });
    if (changed) this.cache$.next(next);
  }

  displayNameOf(u: any): string {
    return u?.displayName || u?.anzeigeName || u?.name || u?.email || '';
  }

  private sortUsers(list: FirestoreUser[]): FirestoreUser[] {
    return [...(list ?? [])].sort((a, b) =>
      this.displayNameOf(a).localeCompare(this.displayNameOf(b), 'de', { sensitivity: 'base' }),
    );
  }

  // Groups

  ensureGroupsLoaded(): void {
    const ctxId = this.settingsService.selectedUserContext$?.value?.identifier;
    if (!ctxId) return;
    const contextIds = this.assignableContextIds(ctxId);
    const key = [...contextIds].sort().join(',');
    if (this.groupsLoadedFor === key) return;
    this.groupsLoadedFor = key;
    this.userGroupsService.observeDataGroups(ctxId);
  }

  get currentGroups(): UserGroup[] {
    return this.sortGroups(this.userGroupsService.allAssignableUserGroupsSubject$.value ?? []);
  }

  groupDisplayName(g: UserGroup): string {
    return g?.name ?? g?.identifier ?? '';
  }

  loadMembersOfGroup(groupId: string): Promise<string[]> {
    return this.loadGroupMembers(groupId);
  }

  private loadGroupMembers(groupId: string): Promise<string[]> {
    if (!groupId) return Promise.resolve([]);

    const cached = this.groupMembers.get(groupId);
    if (cached) return Promise.resolve(cached);

    const existing = this.groupMemberLoads.get(groupId);
    if (existing) return existing;

    const load = getDocs(query(
      collection(this.firestore, NetworkConstants.COLLECTION_USER),
      where('userGroupIds', 'array-contains', groupId),
    ))
      .then(snap => snap.docs.map(d => d.id))
      .catch(err => {
        console.error(`[UserNameLookup] member query for group ${groupId} failed:`, err);
        return (this.usersSubject$.value ?? [])
          .filter((u: any) => u?.userGroupIds?.includes(groupId))
          .map((u: any) => u.identifier)
          .filter((id: any): id is string => !!id);
      })
      .then(members => {
        this.groupMembers.set(groupId, members);
        this.groupMemberLoads.delete(groupId);
        return members;
      });

    this.groupMemberLoads.set(groupId, load);
    return load;
  }

  groupPseudoId(groupId: string): string {
    return `${GROUP_ID_PREFIX}${groupId}`;
  }

  isGroupId(id: string): boolean {
    return typeof id === 'string' && id.startsWith(GROUP_ID_PREFIX);
  }

  groupIdOf(pseudoId: string): string {
    return pseudoId.slice(GROUP_ID_PREFIX.length);
  }

  expandAssigneeSelection(raw: (string | null | undefined)[] | null | undefined): string[] {
    const out = new Set<string>();
    (raw ?? []).forEach(id => {
      if (!id) return;
      if (this.isGroupId(id)) {
        const groupId = this.groupIdOf(id);
        this.loadGroupMembers(groupId);
        (this.groupMembers.get(groupId) ?? []).forEach(uid => out.add(uid));
      } else {
        out.add(id);
      }
    });
    return [...out];
  }

  private sortGroups(list: UserGroup[]): UserGroup[] {
    return [...list].sort((a, b) =>
      this.groupDisplayName(a).localeCompare(this.groupDisplayName(b), 'de', { sensitivity: 'base' }),
    );
  }
}